import React from 'react';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from '@material-ui/core';

import {
    AmountTriggerFormState,
    DEFAULT_TRIGGER,
    EQUALITY_CONDITION_LABELS,
    EqualityCondition,
    TRIGGER_TYPE_LABELS,
    TriggerType,
} from '../lib/koronaPromotionConfig';

interface Props {
    form: AmountTriggerFormState;
    onChange: (form: AmountTriggerFormState) => void;
    /** When true, lock to compound product+receipt trigger (Buy X Get Y). */
    lockCompoundProductTrigger?: boolean;
}

const AMOUNT_TRIGGER_TYPES: TriggerType[] = [
    'productAmount',
    'receiptProductAmount',
    'productAndReceiptAmount',
    'receiptRevenue',
    'tagAmount',
    'tagRevenue',
];

const availableTriggerTypes = Object.entries(TRIGGER_TYPE_LABELS);

const TriggerConfigForm: React.FC<Props> = ({ form, onChange, lockCompoundProductTrigger = false }) => {
    const effectiveType = lockCompoundProductTrigger ? 'productAndReceiptAmount' : form.triggerType;
    const showAmountFields = AMOUNT_TRIGGER_TYPES.includes(effectiveType);
    const showProductField = effectiveType === 'productAmount' || effectiveType === 'productAndReceiptAmount';
    const showTagField = effectiveType === 'tagAmount' || effectiveType === 'tagRevenue';
    const showCustomerGroups = effectiveType === 'customerGroups';
    const showCustomers = effectiveType === 'customers';

    return (
        <>
            <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                When does this promo apply?
            </Typography>
            {!lockCompoundProductTrigger && (
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="trigger-type-label">Trigger type</InputLabel>
                    <Select
                        labelId="trigger-type-label"
                        label="Trigger type"
                        value={form.triggerType}
                        onChange={(e) => onChange({ ...form, triggerType: e.target.value as TriggerType })}
                    >
                        {availableTriggerTypes.map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {lockCompoundProductTrigger && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Trigger: product quantity and receipt product quantity (both ≥ minimum).
                </Typography>
            )}

            {showAmountFields && (
                <>
                    <TextField
                        label={
                            effectiveType === 'receiptRevenue' || effectiveType === 'tagRevenue'
                                ? 'Minimum amount ($)'
                                : 'Minimum quantity'
                        }
                        type="number"
                        value={form.value}
                        onChange={(e) => onChange({ ...form, value: e.target.value })}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        inputProps={{
                            min: 0,
                            step: effectiveType.includes('Revenue') ? 0.01 : 1,
                        }}
                        disabled={lockCompoundProductTrigger}
                        helperText={
                            lockCompoundProductTrigger
                                ? 'Derived from Buy X Get Y quantities.'
                                : undefined
                        }
                    />
                    <FormControl variant="outlined" fullWidth margin="dense" disabled={lockCompoundProductTrigger}>
                        <InputLabel id="equality-condition-label">Condition</InputLabel>
                        <Select
                            labelId="equality-condition-label"
                            label="Condition"
                            value={form.equalityCondition}
                            onChange={(e) =>
                                onChange({ ...form, equalityCondition: e.target.value as EqualityCondition })
                            }
                        >
                            {Object.entries(EQUALITY_CONDITION_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )}

            {showProductField && !lockCompoundProductTrigger && (
                <TextField
                    label="Product UUID (optional)"
                    value={form.productId}
                    onChange={(e) => onChange({ ...form, productId: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="Leave blank to use the product selected when creating a tracker."
                />
            )}

            {showTagField && (
                <TextField
                    label="Tag UUID"
                    value={form.tagId}
                    onChange={(e) => onChange({ ...form, tagId: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                />
            )}

            {showCustomerGroups && (
                <TextField
                    label="Customer group UUIDs"
                    value={form.customerGroupIds}
                    onChange={(e) => onChange({ ...form, customerGroupIds: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="Comma-separated Korona customer group UUIDs."
                />
            )}

            {showCustomers && (
                <TextField
                    label="Customer UUIDs"
                    value={form.customerIds}
                    onChange={(e) => onChange({ ...form, customerIds: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="Comma-separated Korona customer UUIDs."
                />
            )}

            <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Attach external coupon code (required)"
            />
        </>
    );
};

export { DEFAULT_TRIGGER };
export default TriggerConfigForm;
